import React from "react";
import ReferenceBuilder from "./ReferenceBuilder";
import ViewHeader from "./ViewHeader";
import PaneStatus from "../../../components/feedback/PaneStatus";
import { Alert } from "../../../components/ui/alert";
import { DocumentReference } from "../../../platform/word/documentTools";
import { useGetReferencesQuery } from "../api/playbookApi";
import { AssemblyType } from "../types/assembly-model";
import { AssemblyReferenceModel, AssemblyReferenceWriteModel } from "../types/assembly-reference";
import { useAssemblyContext } from "../context/AssemblyContext";

interface EditReferenceViewProps {
  assemblyType: AssemblyType;
  assignedGroupObjectIdentifier?: string;
  selectedReference: DocumentReference;
  onBack: () => void;
  onUpdateReference: (
    model: AssemblyReferenceWriteModel,
    promptLabel: string,
    existing: AssemblyReferenceModel
  ) => Promise<void>;
  onDeleteReference: (existing: AssemblyReferenceModel) => Promise<void>;
  loading: boolean;
  error: boolean;
}

const EditReferenceView = ({
  assemblyType,
  assignedGroupObjectIdentifier,
  selectedReference,
  onBack,
  onUpdateReference,
  onDeleteReference,
  loading,
  error,
}: EditReferenceViewProps) => {
  const ctx = useAssemblyContext();
  const { data: references, isLoading, error: refError } = useGetReferencesQuery({ ctx });

  // The Word SDT is tagged with the stable identifier; resolve the full
  // reference (which carries the API objectIdentifier) from the list.
  const referenceData = references?.find(
    (r) => r.stableObjectIdentifier === selectedReference.stableObjectIdentifier
  );

  return (
    <div className="flex flex-col gap-4 px-3.5 pt-3">
      <ViewHeader title="Edit reference" onBack={onBack} />

      {isLoading ? (
        <PaneStatus loading title="Loading the reference…" />
      ) : refError ? (
        <Alert severity="error">Failed to load reference data. Please try again.</Alert>
      ) : !referenceData ? (
        <Alert severity="warning">
          This reference has been deleted and is no longer available for editing.
        </Alert>
      ) : (
        <ReferenceBuilder
          assemblyType={assemblyType}
          assignedGroupObjectIdentifier={assignedGroupObjectIdentifier}
          onSubmit={(model, promptLabel) => onUpdateReference(model, promptLabel, referenceData)}
          onDelete={() => onDeleteReference(referenceData)}
          loading={loading}
          error={error}
          onClose={onBack}
          existingReference={referenceData}
          isEditMode
        />
      )}
    </div>
  );
};

export default EditReferenceView;
